<template>
  <GChart
    :settings="{packages: ['corechart']}"
    :version="'42'"
    :createChart="createChart"
    class="base-bar-chart"
  />
</template>

<script>
import { GChart } from 'vue-google-charts'
import _ from 'lodash'
import { mapState } from 'vuex'

/**
 * Base Component for Area Chart
 */
export default {
  name: 'BaseBarChart',
  components: {
    GChart
  },
  mounted () {
    window.addEventListener('resize', this.resizeListener)
  },
  beforeDestroy () {
    window.removeEventListener('resize', this.resizeListener)
  },
  props: {
    chartData: {
      type: Object,
      required: true
    },
    showVerticalGridlines: {
      type: Boolean,
      default: true
    }
  },
  watch: {
    chartData: {
      handler(){
        setTimeout(() => this.createChart(this.el, this.google), 500)
      },
      deep: true
    },
    isNavbarCollapsed () {
      this.resizeListener()
    },
    showVerticalGridlines () {
      this.resizeListener()
    }
  },
  data () {
    return {
      google: null,
      el: null,
      chart: null,
      debounced: _.throttle(() => {
        this.createChart(this.el, this.google)
      }, 100, { leading: true, trailing: true })
    }
  },
  computed: {
    ...mapState([
      'isNavbarCollapsed'
    ])
  },
  methods: {
    /* the chart should redraw after resizing browser window */
    resizeListener () {
      this.debounced.flush()
      this.debounced()
    },
    /**
     * Callback for createChart property of vue-google-charts library
     * Consider isStacked property in options object manages the way of rendering the chart
     * {@see https://developers.google.com/chart/interactive/docs/gallery/areachart#stacking-areas}
     * all list of available options {@see https://developers.google.com/chart/interactive/docs/gallery/areachart#configuration-options}
     * prepared data passed into chat using DataView {@link https://developers.google.com/chart/interactive/docs/datatables_dataviews}
     * @param el - chart html element
     * @param google - link to the Google Chart library
     */
    createChart (el, google) {
      if (!google || !el || !this.chartData || !this.chartData.data) {
        return
      }
      if (!this.google) {
        this.google = google
        this.el = el
      }
      if (this.chart) {
        this.chart.clearChart()
      }
      const data = google.visualization.arrayToDataTable(this.chartData.data)
      const view = new google.visualization.DataView(data)
      const options = {
        colors: this.chartData.colorSchema,
        legend: 'none',
        isStacked: true,
        backgroundColor: 'transparent',
        height: 300,
        width: '100%',
        chartArea: { width: '92%', height: '80%', left: '5%', top: 10 },
        label: 'none',
        tooltip: {
          trigger: 'focus',
          isHtml: true
        },
        fontName: 'Roboto',
        fontSize: 12,
        interpolateNulls: true,
        hAxis: {
          ticks: [...this.chartData.ticks],
          format: 'y-M-d',
          gridlines: {
            count: 30,
            color: this.showVerticalGridlines ? '#b2b9be' : 'transparent'
          },
          baseLineColor: '#b2b9be',
          textStyle: {
            color: '#b2b9be',
            fontSize: 10
          }
        },
        vAxis: {
          gridlines: {
            count: 5
          },
          baseLineColor: '#b2b9be',
          viewWindow: {
            min: 0
          },
          textStyle: {
            color: '#b2b9be',
            fontSize: 12,
            fontWeight: 500,
            bold: true
          }
        }
      }
      this.chart = new google.visualization.AreaChart(el)
      google.visualization.events.removeAllListeners(this.chart)

      google.visualization.events.addListener(this.chart, 'select', () => {
        this.chart.setSelection(null)
      })
      // we need it to prevent appearance errors on the screen
      google.visualization.events.addListener(this.chart, 'error', function (err) {
        google.visualization.errors.removeError(err.id)
      })
      this.chart.draw(view, options)
    }

  }
}

</script>

<style scoped lang="scss">

</style>
